import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { logger } from "@bfc-software/app-toolbox";
import { OrgCfgSchemaTypes } from "@bfc-software/api-schemas";
import type { selectState as ss } from "@bfc-software/pallet";
import {
  useOrgCfgFetchClient,
  type OrgCfgFetchClient,
} from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";

import { formatTimezoneLabel } from "./timezones";
import {
  endMinuteAfter,
  minuteToTimeString,
  timeStringToMinute,
} from "./shift-time";

/**
 * Real client against `/orgs/warehouse_settings/*` (org_config.yaml), which
 * carries this screen's whole record -- system settings, the per-user
 * profile defaults and the shift list all live in one
 * `appWarehouseSettings.settings` blob (`selectWarehouseSettings`), plus
 * `warehouseTz` alongside it. Every hook below reads or writes that same
 * blob, projected for the card that calls it, rather than three independent
 * resources -- which is why they all share one query key
 * (`warehouseSettingsQueryKey`) and one mutate-by-refetch path
 * (`useWarehouseSettingsMutation`): a save from any one card has to
 * invalidate what the other two are showing.
 */

type Schemas = OrgCfgSchemaTypes.components["schemas"];
type AppWarehouseSettings = Schemas["appWarehouseSettings"];
type SelectWarehouseSettings = Schemas["selectWarehouseSettings"];
type ShiftRecord = Schemas["shiftRecord"];

/**
 * Same scope as `selectors.tsx`'s and `user-groups.tsx`'s `APP_NAME` -- this
 * screen is Outbound's, so it only ever reads/writes the "select" shape of
 * `warehouseSettings`.
 */
const APP_NAME: Schemas["appName"] = "select";

export interface SystemSettings {
  validationAccuracyThresholdPercent: number;
  observationWindowDurationDays: number;
  confidenceGateScanCount: number;
}

/**
 * Mirrors `VALIDATION_OPTIONS`/`TASK_TYPE_OPTIONS`/`BAND_OPTIONS`/
 * `SUBSTITUTION_OPTIONS` in `selectors.tsx` -- this card sets the values a new
 * selector's own settings start from, so the two option sets have to agree.
 * Also mirrors org_config.yaml's `selectValidationLevel`/
 * `selectTaskTypeEligibility`/`selectDifficultyBandCap` enums exactly, which
 * is what lets `set()` below cast a selected option straight into the wire
 * type with no translation table.
 */
export const VALIDATION_LEVEL_OPTIONS: ss.NonEmptyArray<string> = [
  "Standard",
  "Enhanced",
];
export const TASK_TYPE_ELIGIBILITY_OPTIONS: ss.NonEmptyArray<string> = [
  "All",
  "Runner only",
  "Regular only",
];
export const DIFFICULTY_BAND_OPTIONS: ss.NonEmptyArray<string> = [
  "B1",
  "B2",
  "B3",
  "B4",
  "B5",
];
export const SUBSTITUTION_AUTHORIZED_OPTIONS: ss.NonEmptyArray<string> = [
  "No",
  "Yes",
];

export interface UserProfileDefaults {
  defaultValidationLevel: string;
  defaultTaskTypeEligibility: string;
  defaultDifficultyBandCap: string;
  defaultSubstitutionAuthorized: string;
}

export interface Shift {
  id: string;
  name: string;
  /** Minutes since local midnight, `[0, 1440)`. */
  startMinute: number;
  /**
   * Minutes since local midnight, `[0, 2880)` -- may exceed 1440 for a shift
   * that crosses midnight (Night: 22:00-06:30 is stored as 1350-1590).
   */
  endMinute: number;
}

export interface ShiftsState {
  /** IANA zone name; `warehouseTimezoneLabel` is its "(UTC-4)" display pair. */
  warehouseTimezone: string;
  warehouseTimezoneLabel: string;
  shifts: Shift[];
}

export interface ShiftInput {
  name: string;
  startMinute: number;
  endMinute: number;
}

function warehouseSettingsQueryKey(orgId: string, warehouseId: string | null) {
  return ["system-controls", "warehouse-settings", orgId, warehouseId] as const;
}

async function fetchWarehouseSettings(
  client: OrgCfgFetchClient,
  orgId: string,
  warehouseId: string,
): Promise<AppWarehouseSettings> {
  const { data, error } = await client.GET("/orgs/warehouse_settings/get", {
    params: { query: { orgId, warehouseId, appName: APP_NAME } },
  });
  if (error || !data)
    throw error ?? new Error("Failed to load system controls");
  return data;
}

/**
 * `settings` is typed as `selectWarehouseSettings | deliverWarehouseSettings`
 * (org_config.yaml's `warehouseSettings` oneOf) because the wire shape is
 * keyed on `appName`. This screen only ever asks for `APP_NAME`, so a
 * response missing the "select" shape means the service and this screen have
 * drifted -- surfaced as a thrown error rather than silently rendering an
 * empty card.
 */
function selectSettingsOf(
  record: AppWarehouseSettings,
): SelectWarehouseSettings {
  const settings = record.settings;
  // TS can't narrow the `oneOf` away from `deliverWarehouseSettings` via `in`
  // -- an index-signature type (`Record<string, never>`) never gets excluded
  // by a property-presence check -- so the cast below is asserting what the
  // guard just checked at runtime, not skipping the check.
  if (!settings || !("systemSettings" in settings)) {
    throw new Error(
      `Warehouse settings for appName "${APP_NAME}" did not have the expected shape`,
    );
  }
  return settings as SelectWarehouseSettings;
}

async function saveWarehouseSettings(
  client: OrgCfgFetchClient,
  orgId: string,
  warehouseId: string,
  current: AppWarehouseSettings,
  next: { settings?: SelectWarehouseSettings; warehouseTz?: string },
): Promise<void> {
  const { error } = await client.POST("/orgs/warehouse_settings/update", {
    body: {
      orgId,
      warehouseId,
      appName: APP_NAME,
      warehouseTz: next.warehouseTz ?? current.warehouseTz,
      settings: next.settings ?? selectSettingsOf(current),
    },
  });
  if (error) throw error;
}

/**
 * Every mutation here follows the same shape: fetch the current record
 * fresh (rather than trust the cache, which may be stale against a second
 * tab or a teammate's edit), let `apply` build the patched settings/
 * warehouseTz plus whatever the caller's `onSuccess` wants back, save it,
 * then invalidate so every card reading this warehouse's settings refetches.
 */
function useWarehouseSettingsMutation<Patch, Result>(
  orgId: string,
  warehouseId: string | null,
  apply: (
    current: AppWarehouseSettings,
    patch: Patch,
  ) => {
    settings?: SelectWarehouseSettings;
    warehouseTz?: string;
    result: Result;
  },
) {
  const queryClient = useQueryClient();
  const client = useOrgCfgFetchClient();
  return useMutation({
    mutationFn: async (patch: Patch) => {
      if (typeof warehouseId !== "string") {
        throw new Error(
          "Cannot save system controls before the session has a warehouse",
        );
      }
      const current = await fetchWarehouseSettings(client, orgId, warehouseId);
      const { result, ...next } = apply(current, patch);
      await saveWarehouseSettings(client, orgId, warehouseId, current, next);
      return result;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: warehouseSettingsQueryKey(orgId, warehouseId),
      }),
  });
}

function toSystemSettings(
  systemSettings: SelectWarehouseSettings["systemSettings"],
): SystemSettings {
  return {
    validationAccuracyThresholdPercent:
      systemSettings.validationAccuracyThreshold,
    observationWindowDurationDays: systemSettings.observationWindowDays,
    confidenceGateScanCount: systemSettings.confidenceGateScanCt,
  };
}

function withSystemSettingsPatch(
  current: SelectWarehouseSettings["systemSettings"],
  patch: Partial<SystemSettings>,
): SelectWarehouseSettings["systemSettings"] {
  return {
    validationAccuracyThreshold:
      patch.validationAccuracyThresholdPercent ??
      current.validationAccuracyThreshold,
    observationWindowDays:
      patch.observationWindowDurationDays ?? current.observationWindowDays,
    confidenceGateScanCt:
      patch.confidenceGateScanCount ?? current.confidenceGateScanCt,
  };
}

export function useSystemSettings(orgId: string, warehouseId: string | null) {
  const client = useOrgCfgFetchClient();
  return useQuery({
    queryKey: warehouseSettingsQueryKey(orgId, warehouseId),
    queryFn: () => fetchWarehouseSettings(client, orgId, warehouseId!),
    enabled: typeof warehouseId === "string",
    select: (record) =>
      toSystemSettings(selectSettingsOf(record).systemSettings),
  });
}

export function useUpdateSystemSettings(
  orgId: string,
  warehouseId: string | null,
) {
  return useWarehouseSettingsMutation<Partial<SystemSettings>, SystemSettings>(
    orgId,
    warehouseId,
    (current, patch) => {
      const currentSettings = selectSettingsOf(current);
      const systemSettings = withSystemSettingsPatch(
        currentSettings.systemSettings,
        patch,
      );
      return {
        settings: { ...currentSettings, systemSettings },
        result: toSystemSettings(systemSettings),
      };
    },
  );
}

function toUserProfileDefaults(
  defaultProfile: SelectWarehouseSettings["defaultProfile"],
): UserProfileDefaults {
  return {
    defaultValidationLevel: defaultProfile.validationLevel,
    defaultTaskTypeEligibility: defaultProfile.taskTypeEligibility,
    defaultDifficultyBandCap: defaultProfile.difficultyBandCap,
    defaultSubstitutionAuthorized: defaultProfile.subsAuthorized ? "Yes" : "No",
  };
}

function withUserProfileDefaultsPatch(
  current: SelectWarehouseSettings["defaultProfile"],
  patch: Partial<UserProfileDefaults>,
): SelectWarehouseSettings["defaultProfile"] {
  return {
    validationLevel: (patch.defaultValidationLevel ??
      current.validationLevel) as SelectWarehouseSettings["defaultProfile"]["validationLevel"],
    taskTypeEligibility: (patch.defaultTaskTypeEligibility ??
      current.taskTypeEligibility) as SelectWarehouseSettings["defaultProfile"]["taskTypeEligibility"],
    difficultyBandCap: (patch.defaultDifficultyBandCap ??
      current.difficultyBandCap) as SelectWarehouseSettings["defaultProfile"]["difficultyBandCap"],
    subsAuthorized:
      patch.defaultSubstitutionAuthorized !== undefined
        ? patch.defaultSubstitutionAuthorized === "Yes"
        : current.subsAuthorized,
  };
}

export function useUserProfileDefaults(
  orgId: string,
  warehouseId: string | null,
) {
  const client = useOrgCfgFetchClient();
  return useQuery({
    queryKey: warehouseSettingsQueryKey(orgId, warehouseId),
    queryFn: () => fetchWarehouseSettings(client, orgId, warehouseId!),
    enabled: typeof warehouseId === "string",
    select: (record) =>
      toUserProfileDefaults(selectSettingsOf(record).defaultProfile),
  });
}

export function useUpdateUserProfileDefaults(
  orgId: string,
  warehouseId: string | null,
) {
  return useWarehouseSettingsMutation<
    Partial<UserProfileDefaults>,
    UserProfileDefaults
  >(orgId, warehouseId, (current, patch) => {
    const currentSettings = selectSettingsOf(current);
    const defaultProfile = withUserProfileDefaultsPatch(
      currentSettings.defaultProfile,
      patch,
    );
    return {
      settings: { ...currentSettings, defaultProfile },
      result: toUserProfileDefaults(defaultProfile),
    };
  });
}

/** `"shift-<index>"` -- `shiftRecord` carries no id of its own, so the list's position in `shiftList` is the only stable handle a row has within one render of this screen. */
function shiftIdOf(index: number): string {
  return `shift-${index}`;
}

function shiftIndexOf(id: string): number {
  return Number(id.slice("shift-".length));
}

function toShift(record: ShiftRecord, index: number): Shift {
  const startMinute = timeStringToMinute(record.startTime);
  const rawEndMinute = timeStringToMinute(record.endTime);
  return {
    id: shiftIdOf(index),
    name: record.name,
    startMinute,
    endMinute: endMinuteAfter(startMinute, rawEndMinute),
  };
}

function toShiftRecord(input: ShiftInput): ShiftRecord {
  return {
    name: input.name,
    startTime: minuteToTimeString(input.startMinute),
    endTime: minuteToTimeString(input.endMinute),
  };
}

function toShiftsState(
  settings: SelectWarehouseSettings,
  warehouseTz: string | undefined,
): ShiftsState {
  const timezone = warehouseTz ?? "unknown";
  return {
    warehouseTimezone: timezone,
    warehouseTimezoneLabel: formatTimezoneLabel(timezone),
    shifts: settings.shiftList.map(toShift),
  };
}

export function useShifts(orgId: string, warehouseId: string | null) {
  const client = useOrgCfgFetchClient();
  return useQuery({
    queryKey: warehouseSettingsQueryKey(orgId, warehouseId),
    queryFn: () => fetchWarehouseSettings(client, orgId, warehouseId!),
    enabled: typeof warehouseId === "string",
    select: (record) =>
      toShiftsState(selectSettingsOf(record), record.warehouseTz),
  });
}

export function useUpdateWarehouseTimezone(
  orgId: string,
  warehouseId: string | null,
) {
  return useWarehouseSettingsMutation<string, ShiftsState>(
    orgId,
    warehouseId,
    (current, timezone) => ({
      warehouseTz: timezone,
      result: toShiftsState(selectSettingsOf(current), timezone),
    }),
  );
}

export function useCreateShift(orgId: string, warehouseId: string | null) {
  return useWarehouseSettingsMutation<ShiftInput, Shift>(
    orgId,
    warehouseId,
    (current, input) => {
      const currentSettings = selectSettingsOf(current);
      const shiftList = [...currentSettings.shiftList, toShiftRecord(input)];
      return {
        settings: { ...currentSettings, shiftList },
        result: { id: shiftIdOf(shiftList.length - 1), ...input },
      };
    },
  );
}

export function useUpdateShift(orgId: string, warehouseId: string | null) {
  return useWarehouseSettingsMutation<
    ShiftInput & { id: string },
    ShiftInput & { id: string }
  >(orgId, warehouseId, (current, { id, ...input }) => {
    const currentSettings = selectSettingsOf(current);
    const index = shiftIndexOf(id);
    const shiftList = currentSettings.shiftList.map((record, i) =>
      i === index ? toShiftRecord(input) : record,
    );
    return {
      settings: { ...currentSettings, shiftList },
      result: { id, ...input },
    };
  });
}

export function useDeleteShift(orgId: string, warehouseId: string | null) {
  return useWarehouseSettingsMutation<string, string>(
    orgId,
    warehouseId,
    (current, id) => {
      const currentSettings = selectSettingsOf(current);
      const index = shiftIndexOf(id);
      const shiftList = currentSettings.shiftList.filter((_, i) => i !== index);
      logger.info("Removing shift from warehouse settings", { id, index });
      return { settings: { ...currentSettings, shiftList }, result: id };
    },
  );
}
